import { DeleteIcon, EditIcon, LinkIcon } from '@chakra-ui/icons';
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Box, Button, Flex, Heading, IconButton, Radio, RadioGroup, Stack, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import SortIndicator from 'components/table/SortIndicator';
import TableDensityToggle from 'components/table/TableDensityToggle';
import { densityTokens, type TableDensity } from '../../../theme/chart-config';

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEvent } from 'react-icons/md';
import { CalendarIllustration } from 'components/illustrations';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { EventDto } from '../../../models/event/dto/event.dto';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useCancelEvent } from '../hooks/useCancelEvent';
import { useCancelRecurringGroup } from '../hooks/useCancelRecurringGroup';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useGetEvent } from '../hooks/useGetEvent';
import { useGetEvents } from '../hooks/useListEvents';
import CreateEventForm from './CreateEventForm';
import ExportAttendanceModal from './ExportAttendanceModal';
import { typography } from 'theme/tokens';
import { tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';

const columnHelper = createColumnHelper<EventDto>();

export default function EventList() {
    const { selectedLocation } = useSelectedLocation();
    const [direction, setDirection] = useState<'past' | 'future'>('future');
    const { data, isFetching, isError, isInitialLoading } = useGetEvents(selectedLocation?.id, direction);
    // `enabled: !!locationId`, and an org with no locations — or one whose
    // locations read was refused — never selects one, because `SearchBar` only
    // auto-selects from a non-empty answer (measured run 100). The loader
    // below used to be `(isLoading || isFetching) && length === 0`, which is
    // permanently true in that state, and the EmptyState under it says "no
    // events yet — create your first" about a read that was never sent.
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [density, setDensity] = useState<TableDensity>('comfortable');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const segmentBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const segmentActiveBg = useColorModeValue('brand.500', 'brand.400');
    const segmentActiveText = useColorModeValue('white', 'white');
    const segmentInactiveText = useColorModeValue('gray.500', 'gray.400');
    const segmentHoverBg = useColorModeValue('gray.200', 'whiteAlpha.200');
    const navigation = useNavigate();
    const toast = useToast();

    // Edit modal
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const [editingEvent, setEditingEvent] = useState<EventDto | null>(null);

    // Delete modals
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingEvent, setDeletingEvent] = useState<EventDto | null>(null);
    const [deleteOption, setDeleteOption] = useState<'single' | 'future' | 'all'>('single');
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // The roster behind the "N participants will be affected" warning below.
    //
    // It cannot come from `deletingEvent`: that row is served by
    // `GET /events/list/:direction`, which populates neither `participants` nor
    // `teams`, and `EventEntity.toDto()` serialises an unpopulated collection as
    // `[]`. So `deletingEvent.participants.length` was 0 on every event ever
    // listed, and this warning had never once rendered — on the one dialog whose
    // whole purpose is telling an admin what a cancellation will break.
    // `GET /events/:id` populates the roster (`participants.*`), so ask it, and
    // only when a dialog is actually open. Both halves are pinned API-side in
    // `event-roster-on-read-paths.spec.ts`.
    const { data: deletingEventDetails, isLoading: isLoadingRoster } = useGetEvent(deletingEvent?.id ?? 0);
    // react-query v4 reports `isLoading` for a *disabled* query too, so the
    // dialog's open state is what makes this mean "still fetching".
    const isRosterPending = !!deletingEvent && isLoadingRoster;
    // Fall back to the list row rather than to 0: if the list endpoint ever does
    // populate the roster, this keeps working, and it degrades to today's
    // behaviour instead of a crash if the detail fetch fails.
    const affectedParticipantCount =
        deletingEventDetails?.participants?.length ?? deletingEvent?.participants?.length ?? 0;

    // Mutations
    const { mutateAsync: cancelEvent, isLoading: isCancelling } = useCancelEvent();
    const { mutateAsync: cancelRecurringGroup, isLoading: isCancellingGroup } = useCancelRecurringGroup();

    const handleEditEvent = (event: EventDto) => {
        setEditingEvent(event);
        onEditOpen();
    };

    const handleCloseEdit = () => {
        setEditingEvent(null);
        onEditClose();
    };

    const handleDeleteEvent = (event: EventDto) => {
        setDeletingEvent(event);
        setDeleteOption('single'); // Reset to default
        onDeleteOpen();
    };

    const handleShareEvent = async (event: EventDto) => {
        const eventLink = event.sharableLink;
        if (!eventLink) {
            toast({
                title: translate('event.link_not_available'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(eventLink);
            toast({
                title: translate('event.link_copied'),
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate('event.copy_failed'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleConfirmDelete = async () => {
        if (!deletingEvent) return;

        try {
            if (deletingEvent.recurringGroupId && deleteOption !== 'single') {
                // Delete recurring group (future or all)
                const result = await cancelRecurringGroup({
                    recurringGroupId: deletingEvent.recurringGroupId,
                    options: {
                        futureOnly: deleteOption === 'future',
                        notifyParticipants: true,
                    },
                });

                toast({
                    title: translate('event.delete_success'),
                    description: `${result.cancelledCount} / ${result.totalEvents} ${translate('event.delete_success')}`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                // Delete single event
                await cancelEvent(deletingEvent.id);

                toast({
                    title: translate('event.delete_success'),
                    description: translate('event.delete_success'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }

            onDeleteClose();
            setDeletingEvent(null);
        } catch (error: any) {
            toast({
                title: translate('event.delete_error'),
                description: error.message || translate('event.delete_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('title')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('location', {
            id: 'location',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.location')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('courts', {
            id: 'courts',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.court')}
                </Text>
            ),
            cell: (info: any) => {
                const courts = info.getValue() || [];
                return (
                    <Flex align='center'>
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {courts.length > 0 ? courts.map((c: any) => c.name).join(', ') : '-'}
                        </Text>
                    </Flex>
                );
            }
        }),
        columnHelper.accessor('capacity', {
            id: 'capacity',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.capacity')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('startTime', {
            id: 'startDate',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.start_date')}
                </Text>
            ),
            cell: (info: any) => {
                const date = new Date(info.getValue());
                return (
                    <Flex align='center'>
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Text>
                    </Flex>
                )
            }
        }),
        columnHelper.accessor('startTime', {
            id: 'startTime',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.start_time')}
                </Text>
            ),
            cell: (info: any) => {
                const formatted = new Date(info.getValue());
                const value = `${formatted.getHours().toString().padStart(2, '0')}h${formatted.getMinutes().toString().padStart(2, '0')}`;
                return (
                    <Flex align='center'>
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {value}
                        </Text>
                    </Flex>
                )
            }
        }),
        columnHelper.accessor('sportType', {
            id: 'sportType',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.sport')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('event.actions')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label={translate('event.copy_link')}
                        icon={<LinkIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleShareEvent(info.row.original);
                        }}
                    />
                    <IconButton
                        aria-label={translate('event.edit_event')}
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent(info.row.original);
                        }}
                    />
                    <IconButton
                        aria-label={translate('event.delete_event')}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(info.row.original);
                        }}
                    />
                </Flex>
            )
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <Card flexDirection='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px='25px' mb="8px" justifyContent='space-between' align='center'>
                <Flex align='center' gap={4}>
                    <Heading as="h2" color={textColor} {...typography.pageTitle}>
                        {translate('tabs.events')}
                    </Heading>
                    <Flex
                        bg={segmentBg}
                        borderRadius='full'
                        p='3px'
                        gap='2px'
                    >
                        <Button
                            size='xs'
                            borderRadius='full'
                            px={4}
                            py={1}
                            fontWeight='600'
                            fontSize='xs'
                            bg={direction === 'past' ? segmentActiveBg : 'transparent'}
                            color={direction === 'past' ? segmentActiveText : segmentInactiveText}
                            _hover={{ bg: direction === 'past' ? segmentActiveBg : segmentHoverBg }}
                            onClick={() => setDirection('past')}
                            transition='all 0.2s ease'
                        >
                            {translate('event.past')}
                        </Button>
                        <Button
                            size='xs'
                            borderRadius='full'
                            px={4}
                            py={1}
                            fontWeight='600'
                            fontSize='xs'
                            bg={direction === 'future' ? segmentActiveBg : 'transparent'}
                            color={direction === 'future' ? segmentActiveText : segmentInactiveText}
                            _hover={{ bg: direction === 'future' ? segmentActiveBg : segmentHoverBg }}
                            onClick={() => setDirection('future')}
                            transition='all 0.2s ease'
                        >
                            {translate('event.future')}
                        </Button>
                    </Flex>
                </Flex>
                <Flex gap={2} align="center">
                    <TableDensityToggle tableId="events" onChange={setDensity} />
                    {/* Deliberately outside the past/future toggle: the export
                        window is picked in its own dialog and spans whatever
                        weeks the club is replanning, not what the table shows. */}
                    <ExportAttendanceModal locationId={selectedLocation?.id} />
                    <CreateEventForm />
                </Flex>
            </Flex>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr
                                key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr
                                    key={row.id}
                                    {...tableRowProps}
                                    onClick={() => navigation(`/admin/eventDetails?event=${row.original.id}`)}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                {...tableCellProps}
                                                fontSize={densityTokens[density].fontSize}
                                                py={densityTokens[density].cellPy}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                {isError && (
                    <ErrorState message={translate('event.error_loading')} />
                )}
                {readState === 'never-issued' && <ScopePrompt gate='location' />}
                {readState === 'answered' && !isFetching && (data || []).length === 0 && (
                    <EmptyState
                        variant="first-use"
                        illustration={<CalendarIllustration />}
                        title={translate('walkthrough.empty_events_title')}
                        description={translate('walkthrough.empty_events_description')}
                    />
                )}
                <Loader isLoading={readState === 'loading'} />
            </Box>

            {/* Edit Event Modal */}
            {editingEvent && (
                <CreateEventForm
                    event={editingEvent}
                    isOpen={isEditOpen}
                    onClose={handleCloseEdit}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {deletingEvent?.recurringGroupId
                                ? translate('event.delete_recurring_events')
                                : translate('event.delete_event')
                            }
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <VStack align="start" spacing={4}>
                                <Text>
                                    {deletingEvent?.recurringGroupId
                                        ? translate('event.delete_recurring_confirmation')
                                        : `${translate('event.delete_confirmation')} "${deletingEvent?.name}"`
                                    }
                                </Text>

                                {deletingEvent?.recurringGroupId && (
                                    <RadioGroup onChange={(value: 'single' | 'future' | 'all') => setDeleteOption(value)} value={deleteOption}>
                                        <Stack spacing={2}>
                                            <Radio value='single'>
                                                {translate('event.delete_this_event_only')}
                                            </Radio>
                                            <Radio value='future'>
                                                {translate('event.delete_future_only')}
                                            </Radio>
                                            <Radio value='all'>
                                                {translate('event.delete_all_events')}
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>
                                )}

                                {affectedParticipantCount > 0 && (
                                    <Text fontSize="sm" color="orange.500" data-testid="delete-participants-warning">
                                        {affectedParticipantCount} {translate('event.participants_count')}
                                    </Text>
                                )}
                            </VStack>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('cancel')}
                            </Button>
                            <Button
                                colorScheme='red'
                                onClick={handleConfirmDelete}
                                ml={3}
                                // Held while the roster is in flight: a warning
                                // that arrives after the admin has confirmed is
                                // the same as no warning. On a failed fetch the
                                // query leaves the loading state, so this cannot
                                // strand the button.
                                isLoading={isCancelling || isCancellingGroup || isRosterPending}
                            >
                                {translate('delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Card>
    );
}
